import { EventEmitter, } from '@spotify-internal/emitter';
import { LRUCache } from '@spotify-internal/linkedlist';
import { logging } from '@js-sdk/debug-tools';
import { Event } from '../enums/event';
import { KeySystem } from '../enums/key_system';
import { Errors } from '../enums/errors';
import { SubtitleMode } from '../enums/subtitle_mode';
import { OperationResult } from '../enums/operation_result';
import { PlaybackError } from '../error/player';
import { EMEManager, } from './eme/manager';
import { EMEConfigs } from './eme/configs';
import { VideoContent } from './video_content';
import { AudioContent } from './audio_content';
import { ContentBuffer, } from './buffer';
import { BandwidthEstimator } from './bandwidth_estimator';
import { getMediaKeySystemConfiguration } from './helpers/eme';
const debugLogger = logging.forTag('playback.player');
/**
 * The amount of time in seconds before the end of the song before marking it as
 * almost ended.
 */
const CAN_PRELOAD_THRESHOLD = 10;
/**
 * The default track cache size, used if it's not overridden in the constructor
 * options.
 */
const TRACK_CACHE_SIZE = 2;
/**
 * Blob URL check.
 */
const BLOB_URL_EXP = /^blob:/;
function _secToMS(seconds) {
    return Math.ceil(seconds * 1000);
}
function _msToSec(ms) {
    return ms / 1000;
}
function hasUriProperty(uri, track) {
    return track && track.hasOwnProperty(uri);
}
function _createPlayerElement(type) {
    return document.createElement(type);
}
function _getMediaConfig(emeManager, player) {
    if (emeManager) {
        return emeManager.getKeySystemInfo().then((keySystemInfo) => {
            const mediaConfig = {
                supports: {
                    protected_audio: !!keySystemInfo.audioFormats.length,
                    unprotected_audio: true,
                    protected_video: !!keySystemInfo.videoFormats.length,
                    unprotected_video: true,
                },
                formats: {
                    audio: keySystemInfo.audioFormats.map((format) => format.contentType),
                    video: keySystemInfo.videoFormats.map((format) => format.contentType),
                },
            };
            if (keySystemInfo.keySystem) {
                mediaConfig.keysystem = keySystemInfo.keySystem;
            }
            if (keySystemInfo.keySystemImpl) {
                mediaConfig.keysystem_impl = keySystemInfo.keySystemImpl;
            }
            return mediaConfig;
        });
    }
    const testVideo = player || document.createElement('video');
    const mediaCapabilities = getMediaKeySystemConfiguration([EMEConfigs[0]], (contentType) => 
    // Currently we only support `probably`, add `maybe` if we need it.
    testVideo.canPlayType(contentType.split(';')[0]) === 'probably');
    if (!mediaCapabilities) {
        return Promise.resolve(null);
    }
    const audioFormats = mediaCapabilities.audioCapabilities.map((cap) => cap.contentType);
    const videoFormats = mediaCapabilities.videoCapabilities.map((cap) => cap.contentType);
    return Promise.resolve({
        supports: {
            protected_audio: false,
            unprotected_audio: !!audioFormats.length,
            protected_video: false,
            unprotected_video: !!videoFormats.length,
        },
        formats: {
            audio: audioFormats,
            video: videoFormats,
        },
    });
}
/**
 * Player: AAC, CENC, MSE, EME.
 *
 * This player can be used to play EME-encoded tracks using MSE, as well as
 * unencrypted tracks.
 */
export class Player extends EventEmitter {
    constructor(options) {
        super();
        /**
         * A flag that indicates whether the subtitles track for a video is visible
         * or not.
         */
        this._subtitlesVisible = false;
        /**
         * A flag that indicates whether the set volume should have a cubic curve
         * applied to it or not.
         */
        this._cubicVolume = false;
        /**
         * String representation of the chosen language for video subtitles.
         */
        this._subtitleLanguage = '';
        /**
         * The player element.
         */
        this._player = null;
        /**
         * An instance of the EMEManager.
         */
        this._emeManager = null;
        /**
         * The Content instance that's currently playing.
         */
        this._currentContent = null;
        /**
         * The next Content instance that will be loaded.
         *
         * We save this as an internal variable so that the loading related functions
         * will be able to access it rather than us passing it around promises, which
         * can lead to memory leaks.
         */
        this._upcomingContent = null;
        /**
         * Map of file-ids being preloaded.
         */
        this._preloadingTracks = {}; // TODO: Check type
        /**
         * An incrementing ID for playback.
         *
         * Every time we play a new track, we increment this value. We then use this
         * value to keep track of operations within the track, such as adding or
         * removing chunks from the buffer.
         */
        this._playId = 0;
        /**
         * A flag that indicates whether a track has been loaded and can be played.
         */
        this._loaded = false;
        /**
         * Indicates whether the media element has been activated.
         */
        this._playerActivated = false;
        /**
         * Handles the throttling timer token for pause events.
         *
         * Audio tags fire a pause event before an ended event, and we do not want to
         * catch that last pause event in most cases. We therefore throttle the pause
         * events to ensure that they don't fire if an ended event is fired.
         */
        this._pauseToken = 0;
        /**
         * The setTimeout token for the synthetic ended event.
         */
        this._syntheticEndedToken = 0;
        /**
         * The currently active codecs concatenated into a string. Used for comparing
         * the next tracks codecs with currents, to determine if the player can be
         * re-used.
         */
        this._activeCodecs = [];
        /**
         * The current volume of the player.
         */
        this._playerVolume = 1;
        /**
         * Sets whether the next native error is unrecoverable.
         */
        this._fatalOnNextError = false;
        /**
         * A flag that indicates whether the 'preload' event can be emitted.
         */
        this._canPreloadEmitted = false;
        /**
         * The position of the player at the time when the last `timeupdate` event
         * was called.
         *
         * Used to guard against cases where the timeupdate is fired multiple times
         * for the same position.
         */
        this._lastTimeUpdatePostion = 0;
        this._audioResolver = options.audioResolver;
        this._clearBufferOnSeek = !!options.clearBufferOnSeek;
        this._createPlayer = options.createPlayer || _createPlayerElement;
        this._cubicVolume = options.cubicVolume;
        this._disableCache = !!options.disableCache;
        this._licenseURLResolver = options.licenseURLResolver;
        this._newBufferPerTrack = !!options.newBufferPerTrack;
        this._newElementPerTrack = !!options.newElementPerTrack;
        this._newMediaKeysPerTrack = !!options.newMediaKeysPerTrack;
        this._rebufferOnQuotaExceeded = !!options.rebufferOnQuotaExceeded;
        this._synthesizeEnded = !!options.synthesizeEnded;
        this._tracker = options.tracker;
        this._transport = options.transport;
        this._videoPlayerContainer = options.videoPlayerContainer;
        this._videoResolver = options.videoResolver;
        this._abrManager = new BandwidthEstimator();
        this._emeManager = options.disallowProtectedTracks
            ? null
            : options.emeManager || null;
        this._cache = new LRUCache(options.trackCacheSize || TRACK_CACHE_SIZE);
        debugLogger.info('Setting Player track cache to:', options.trackCacheSize || TRACK_CACHE_SIZE);
        this._buffer = ContentBuffer.create(this._tracker, undefined, {
            disableBufferAbort: !!options.disableBufferAbort,
            noInit: true,
        });
        // Player Event Handlers
        this._onCanPlay = this._onCanPlay.bind(this);
        this._onCanPlayThrough = this._onCanPlayThrough.bind(this);
        this._onTimeUpdate = this._onTimeUpdate.bind(this);
        this._onDurationChange = this._onDurationChange.bind(this);
        this._onPlaying = this._onPlaying.bind(this);
        this._onPause = this._onPause.bind(this);
        this._onSeeking = this._onSeeking.bind(this);
        this._onEncrypted = this._onEncrypted.bind(this);
        this._onEnded = this._onEnded.bind(this);
        this._onError = this._onError.bind(this);
        this._onRequiresDuration = this._onRequiresDuration.bind(this);
        this._onQuotaExceeded = this._onQuotaExceeded.bind(this);
        this._onLoadedMetadata = this._onLoadedMetadata.bind(this);
        this._onWaiting = this._onWaiting.bind(this);
        this._onPlayedThresholdReached = this._onPlayedThresholdReached.bind(this);
        this._onSyntheticEnded = this._onSyntheticEnded.bind(this);
        this._onLicenseRequestCapped = this._onLicenseRequestCapped.bind(this);
        this._onLicenseRequestError = this._onLicenseRequestError.bind(this);
        this._onNavigatorOffline = this._onNavigatorOffline.bind(this);
        this._onBufferError = this._onBufferError.bind(this);
        this._emitWarning = this._emitWarning.bind(this);
        this._handleLoadingComplete = this._handleLoadingComplete.bind(this);
        this.proxyEmitSync(this._tracker, Event.TRACKER_TRACKING_DATA_CREATED, Event.PLAYER_TRACKING_DATA_CREATED);
        this.proxyEmitSync(this._tracker, Event.TRACKER_TRACKING_DATA_FINALIZED, Event.PLAYER_TRACKING_DATA_FINALIZED);
        this.proxyEmitSync(this._tracker, Event.TRACKER_PLAYBACK_START, Event.PLAYER_PLAYBACK_START);
        this._tracker.on(Event.TRACKER_PLAYED_THRESHOLD_REACHED, this._onPlayedThresholdReached);
        if (this._emeManager) {
            this._emeManager.on(Event.EME_LICENSE_REQUEST_CAPPED, this._onLicenseRequestCapped);
            this._emeManager.on(Event.EME_LICENSE_REQUEST_ERROR, this._onLicenseRequestError);
        }
        if (this._disableCache) {
            debugLogger.info('Cache disabled.');
        }
        this._transport.on(this._transport.EVENT_CONNECTION_OFFLINE, this._onNavigatorOffline);
        this._buffer.on(Event.BUFFER_APPEND_ERROR, this._onBufferError);
        this._buffer.on(Event.BUFFER_QUOTA_EXCEEDED, this._onQuotaExceeded);
        this.proxyEmit(this._buffer, Event.BUFFER_STALLED, Event.PLAYER_BUFFER_STALLED);
        this.proxyEmit(this._buffer, Event.BUFFERING_START, Event.PLAYER_BUFFERING_START);
        this.proxyEmit(this._buffer, Event.BUFFERING_END, Event.PLAYER_BUFFERING_END);
    }
    static create(options) {
        const finalOptions = Object.assign({}, options);
        return EMEManager.create({
            transport: options.transport,
            disallowRobustnessValues: options.disallowRobustnessValues,
            noServerCertificate: options.noServerCertificate,
            precacheServerCertificate: options.precacheServerCertificate,
            unauthServerCertificateBase: options.unauthServerCertificateBase,
            preferredKeySystems: options.preferredKeySystems,
        })
            .then((emeManager) => {
            finalOptions.emeManager = emeManager;
            const player = new Player(finalOptions);
            return finalOptions.preinitMediaElement
                ? player._preinitMediaElement()
                : player;
        })
            .catch((err) => {
            if (options.enableWithoutEME) {
                return Player.createWithOptions(finalOptions);
            }
            throw err;
        });
    }
    static createWithOptions(options) {
        return new Promise((resolve) => {
            const player = new Player(options);
            resolve(options.preinitMediaElement ? player._preinitMediaElement() : player);
        });
    }
    _onLicenseRequestError(e) {
        if (e.data.playId !== this._playId) {
            return;
        }
        const content = this._currentContent;
        if (!content) {
            return;
        }
        const error = e.data.error;
        if (error.shouldRefreshEndpoint) {
            this._licenseURLResolver.remove(content.getKeySystem(), content.getMediaType());
        }
        this._emitError(error, !error.unrecoverable, content.toLogJSON());
    }
    _onLicenseRequestCapped(e) {
        if (e.data.playId !== this._playId) {
            return;
        }
        const content = this._currentContent;
        if (!content) {
            return;
        }
        this.pause();
        this.emit(Event.PLAYER_CAPPED, null);
    }
    /**
     * Determines whether the next MediaError should be unrecoverable.
     *
     * @return True if the next error should be fatal, false otherwise.
     */
    _shouldNextErrorBeFatal() {
        const content = this._currentContent;
        if (!content) {
            return false;
        }
        if (content.isAd() || !content.isProtected()) {
            return false;
        }
        if (!this._player) {
            return false;
        }
        const previousError = this._player.error;
        if (!previousError || !(previousError instanceof MediaError)) {
            // Non-MediaErrors are most likely non-fatal.
            this._fatalOnNextError = false;
            return false;
        }
        switch (previousError.code) {
            case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            case MediaError.MEDIA_ERR_DECODE:
                return true;
            default:
                return false;
        }
    }
    _onNavigatorOffline() {
        this._tracker.trackNavigatorOffline();
    }
    _onBufferError(e) {
        this._emitError(e.data.error, e.data.canPlayNext, e.data.track);
    }
    _addPlayerEvents(player) {
        player.addEventListener(Event.MEDIA_TIMEUPDATE, this._onTimeUpdate);
        player.addEventListener(Event.MEDIA_PLAYING, this._onPlaying);
        player.addEventListener(Event.MEDIA_PAUSE, this._onPause);
        player.addEventListener(Event.MEDIA_SEEKING, this._onSeeking);
        player.addEventListener(Event.MEDIA_ENCRYPTED, this._onEncrypted);
        player.addEventListener(Event.MEDIA_ENDED, this._onEnded);
        player.addEventListener(Event.MEDIA_ERROR, this._onError);
        player.addEventListener(Event.MEDIA_LOADEDMETADATA, this._onLoadedMetadata);
        player.addEventListener(Event.MEDIA_DURATIONCHANGE, this._onDurationChange);
        player.addEventListener(Event.MEDIA_WAITING, this._onWaiting);
        player.addEventListener(Event.MEDIA_CANPLAY, this._onCanPlay);
        player.addEventListener(Event.MEDIA_CANPLAYTHROUGH, this._onCanPlayThrough);
        player.addEventListener(Event.INTERNAL_MEDIA_REQUIRES_DURATION, this._onRequiresDuration);
    }
    _removePlayerEvents(player) {
        player.removeEventListener(Event.MEDIA_TIMEUPDATE, this._onTimeUpdate);
        player.removeEventListener(Event.MEDIA_PLAYING, this._onPlaying);
        player.removeEventListener(Event.MEDIA_PAUSE, this._onPause);
        player.removeEventListener(Event.MEDIA_SEEKING, this._onSeeking);
        player.removeEventListener(Event.MEDIA_ENCRYPTED, this._onEncrypted);
        player.removeEventListener(Event.MEDIA_ENDED, this._onEnded);
        player.removeEventListener(Event.MEDIA_ERROR, this._onError);
        player.removeEventListener(Event.MEDIA_WAITING, this._onWaiting);
        player.removeEventListener(Event.MEDIA_CANPLAY, this._onCanPlay);
        player.removeEventListener(Event.MEDIA_CANPLAYTHROUGH, this._onCanPlayThrough);
        player.removeEventListener(Event.MEDIA_LOADEDMETADATA, this._onLoadedMetadata);
        player.removeEventListener(Event.MEDIA_DURATIONCHANGE, this._onDurationChange);
        player.removeEventListener(Event.INTERNAL_MEDIA_REQUIRES_DURATION, this._onRequiresDuration);
    }
    /**
     * Recreates the audio element, hooks it up with the eme manager and sets it up
     * with new sourcebuffers.
     *
     * @param codecs - The codecs that will be played on this player.
     * @param playId - The id for playback.
     * @param mediaElementType - The type of media element to create.
     * @param disableBuffer - Whether the Buffer class should be disabled.
     * @return A promise that will be resolved true if the player element was created.
     */
    _recreateMediaElement(codecs, playId, mediaElementType, disableBuffer) {
        if (playId !== this._playId) {
            debugLogger.info('Recreate player dropped: playId has changed.');
            return Promise.resolve(false);
        }
        const oldPlayer = this._player;
        if (oldPlayer) {
            if (!this._newElementPerTrack ||
                // This next case is guaranteed not to work, since an HTMLAudioElement
                // would not be able to correctly render video. Therefore we skip
                // trying to reuse the element.
                !(oldPlayer.tagName === 'AUDIO' && mediaElementType === 'video')) {
                // support multiple codecs for one track, since they are separate for
                // video/audio.
                let canPlayCodecs = true;
                for (const codec of codecs) {
                    if (
                    // Special case the Fairplay audio codec, as this will return false
                    // even if it's playable.
                    codec !== 'audio/mp2t;codecs="mp4a.40.2"' &&
                        !oldPlayer.canPlayType(codec)) {
                        // not 'probably' or 'maybe'
                        canPlayCodecs = false;
                        break;
                    }
                }
                if (canPlayCodecs) {
                    debugLogger.info('Reusing media element.');
                    if ((codecs.join(',') !== this._activeCodecs.join(',') ||
                        this._newBufferPerTrack) &&
                        !disableBuffer) {
                        debugLogger.info('Recreating buffer.');
                        this._buffer.recreate(codecs);
                        this._activeCodecs = codecs;
                    }
                    return Promise.resolve(true);
                }
            }
            this._removePlayerEvents(oldPlayer);
            oldPlayer.pause();
            if (this._emeManager) {
                this._emeManager.removeMediaKeys(oldPlayer).catch(function (e) {
                    debugLogger.warn('Failed to remove media keys.', e);
                });
            }
            this._player = null;
            // This is a cleanup for cases where we have back-to-back videos but the
            // video element changes. Note that normally video elements would be
            // removed from the parent when `stop()` is called.
            const videoContainer = this._videoPlayerContainer;
            if (videoContainer && oldPlayer.parentNode === videoContainer) {
                videoContainer.removeChild(oldPlayer);
                this.emit(Event.PLAYER_VIDEO_ELEMENT_REMOVED, null);
            }
        }
        debugLogger.info('Creating new media element.');
        const newPlayer = this._createPlayer(mediaElementType);
        const amount = this._playerVolume;
        newPlayer.autoplay = false;
        newPlayer.loop = false;
        newPlayer.volume = this._cubicVolume ? amount * amount * amount : amount;
        if ('playsInline' in newPlayer) {
            // @ts-ignore
            newPlayer.playsInline = true;
        }
        this._player = newPlayer;
        this._playerActivated = false;
        this._addPlayerEvents(newPlayer);
        if (!disableBuffer) {
            this._buffer.recreate(codecs);
        }
        this._activeCodecs = codecs;
        return Promise.resolve(true);
    }
    /**
     * Returns the container element where the video will be injected.
     *
     * @return An HTMLElement object or null if there's no video container
     */
    _getVideoContainer() {
        if (typeof this._videoPlayerContainer === 'string') {
            return document.querySelector(this._videoPlayerContainer);
        }
        return this._videoPlayerContainer || null;
    }
    /**
     * Handles a "durationchange" event from the HTMLMediaElement.
     */
    _onDurationChange() {
        const playId = this._playId;
        const trigger = () => {
            if (!this._player) {
                return;
            }
            if (playId !== this._playId) {
                // Track changed, don't emit.
                debugLogger.info('Duration changed drop: playId has changed.');
                return;
            }
            const msDuration = _secToMS(this._player.duration);
            const position = _secToMS(this._player.currentTime);
            this._tracker.setActualDuration(msDuration);
            this.emit(Event.PLAYER_DURATION_CHANGED, {
                timestamp: Date.now(),
                position: position,
                duration: msDuration,
            });
        };
        if (this._loaded) {
            trigger();
        }
        else {
            this.once(Event.PLAYER_LOAD, trigger);
        }
    }
    /**
     * Handles a "playing" event from the HTMLMediaElement.
     */
    _onPlaying() {
        if (!this._player) {
            return;
        }
        this._playerActivated = true;
        const content = this._currentContent;
        const position = _secToMS(this._player.currentTime);
        this._tracker.trackPlaying(position);
        this.emit(Event.PLAYER_PLAYING, {
            timestamp: Date.now(),
            position: position,
            logData: content ? content.getLogData() : null,
        });
    }
    /**
     * Handles a "pause" event from the HTMLMediaElement.
     */
    _onPause() {
        if (!this._player) {
            return;
        }
        const position = _secToMS(this._player.currentTime);
        this._tracker.trackPaused(position);
        // Throttle the pause event a few ms so that pause events that are triggered
        // by the track ending are not fired. This prevents the UI from flickering to
        // a paused state.
        const logData = this._currentContent
            ? this._currentContent.getLogData()
            : null;
        this._pauseToken = setTimeout(() => {
            clearTimeout(this._syntheticEndedToken);
            this.emit(Event.PLAYER_PAUSED, {
                position: position,
                logData: logData,
            });
        }, 10);
    }
    /**
     * Handles a "seeking" event from the HTMLMediaElement.
     */
    _onSeeking() {
        this._buffer.abort(this._clearBufferOnSeek);
        this._onTimeUpdate();
    }
    /**
     * This function is called during test
     */
    _onRequiresDuration() {
        if (this._currentContent &&
            this._currentContent.isProtected() &&
            this._player) {
            // @ts-ignore
            this._player.duration = this._currentContent.getCalculatedDuration();
        }
    }
    /**
     * Handles a "quota_exceeded" event from the buffer.
     */
    _onQuotaExceeded() {
        if (!this._player) {
            return;
        }
        if (this._rebufferOnQuotaExceeded) {
            debugLogger.info('Exceeded quota: rebuffering current track.');
            // When this happens, we want to clear all buffers and issue a seek command.
            this._buffer.abort(true);
            this._player.currentTime = this._player.currentTime;
            this._onTimeUpdate();
        }
        else {
            debugLogger.info('Exceeded quota: moving to next track.');
            this._player.pause();
            this._buffer.abort();
            const error = new PlaybackError(Errors.PLAYER_BUFFER_QUOTA_EXCEEDED, 'Buffer quota exceeded.');
            this._emitError(error, true, // Assume that moving to the next track will reset quotas.
            this._currentContent ? this._currentContent.toLogJSON() : undefined);
        }
    }
    /**
     * Handles the "encrypted" event from the HTMLMediaElement.
     *
     * @param ev - The native encrypted event.
     */
    _onEncrypted(ev) {
        debugLogger.info('Got Encrypted event');
        const currentContent = this._currentContent;
        if (!currentContent ||
            currentContent.getKeySystem() !== KeySystem.FAIRPLAY) {
            return;
        }
        this.emitSync(Event.INTERNAL_ENCRYPTED, {
            initData: ev.initData,
            fromPolyfill: !!ev.fromPolyfill,
        });
    }
    /**
     * Handles native "ended" event from the HTMLMediaElement.
     * Ended event for protected track is handled by _onTimeUpdate
     */
    _onEnded() {
        if (!this._currentContent ||
            (this._synthesizeEnded && this._currentContent.isProtected())) {
            return;
        }
        debugLogger.info('Native ended emitted.');
        this._emitEnded();
    }
    /**
     * Handles the timeout for the synthetic ended event.
     */
    _onSyntheticEnded() {
        if (this._synthesizeEnded &&
            this._currentContent &&
            this._currentContent.isProtected()) {
            debugLogger.info('Synthetic ended emitted.');
            this._emitEnded();
        }
    }
    /**
     * Handles an "error" event from the HTMLMediaElement.
     */
    _onError() {
        if (!this._player) {
            return;
        }
        const player = this._player;
        const error = player.error;
        let trackData;
        let isProtected = false;
        const content = this._currentContent;
        let isAd = false;
        if (content) {
            isProtected = content.isProtected();
            trackData = content.toLogJSON();
            isAd = content.isAd();
            content.clearCachedBuffers();
        }
        let code;
        let message;
        let debug;
        let canPlayNext = true;
        // Ads are not protected, and we can skip when the error happens on ads. We
        // also want to determine whether there was a MediaError from the previous
        // track. This prevents some errors from triggering multiple times if there
        // is a severe issue with the system.
        const canPlayIfNotFatal = !content || isAd || !this._fatalOnNextError;
        // Microsoft has extended codes for errors.
        const extendedCode = error && error.msExtendedCode
            ? `0x${(error.msExtendedCode >>> 0).toString(16).toUpperCase()}`
            : null;
        if (error instanceof MediaError) {
            switch (error.code) {
                case MediaError.MEDIA_ERR_ABORTED:
                    code = Errors.MEDIA_ABORTED;
                    message = 'Media aborted.';
                    break;
                case MediaError.MEDIA_ERR_NETWORK:
                    code = Errors.MEDIA_NETWORK_ERROR;
                    message = 'Network error.';
                    break;
                case MediaError.MEDIA_ERR_DECODE:
                    code = Errors.MEDIA_DECODING_ERROR;
                    message = 'Media decoding error.';
                    canPlayNext = canPlayIfNotFatal;
                    break;
                case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                    code = Errors.MEDIA_NOT_SUPPORTED;
                    message = 'Media not supported.';
                    canPlayNext = canPlayIfNotFatal;
                    break;
                default:
                    code = Errors.PLAYER_MEDIA_ERROR;
                    message = 'Media error.';
                    debug = true;
                    break;
            }
            // Chrome and Firefox have started outputing more interesting error
            // messages, so we capture the native message as well.
            //
            // Edge doesn't seem to have a native `error.message` property for some of
            // the errors, however, so we'll just use the extended code, or if that's
            // also not available, stringify the error itself.
            const nativeMessage = error.message || extendedCode || player.src || error.toString();
            message += ` (${nativeMessage})`;
        }
        else {
            code = Errors.PLAYER_PLAYBACK_ERROR;
            // The track-playback-integration tests rely on the posibility to pass
            // along the error message from the (mocked) player.
            // So if it's present: pick it up from the error and use it.
            message = 'Error message undefined';
            debug = true;
        }
        this._buffer.abort(true);
        const playbackError = new PlaybackError(code, message);
        if (!canPlayNext) {
            playbackError.unrecoverable = true;
        }
        // The list player takes note of the number of errors that occur and stops
        // playback after a certain threshold. If the track is an ad, we want the
        // list player to ignore the error.
        playbackError.listPlayerIgnore = isAd;
        // Add some debug logging information
        playbackError.debug.src_url = player.src;
        playbackError.debug.protected = isProtected;
        playbackError.debug.extendedCode = extendedCode;
        playbackError.debug.rawExCode = (error && error.msExtendedCode) || null;
        // Technically error should not be anything other an error object, as this is
        // the standard behaviour. However, in the case where the browser misbehaves
        // and triggers an error without and error object, we should guard against
        // access just to not trigger an error within the error handler.
        if (debug && error) {
            playbackError.debug.nativeCode = error.code || null;
            playbackError.debug.errorData = error;
        }
        this._emitError(playbackError, canPlayNext, trackData);
    }
    _onLoadedMetadata() {
        this.emitSync(Event.INTERNAL_PLAYER_LOADED_METADATA, null);
    }
    _onCanPlay() {
        this.emitSync(Event.INTERNAL_PLAYER_CANPLAY, null);
        this.emit(Event.PLAYER_FIRST_BYTES, null);
    }
    _onCanPlayThrough() {
        this.emitSync(Event.INTERNAL_PLAYER_CANPLAYTHROUGH, null);
    }
    _onWaiting() {
        clearTimeout(this._syntheticEndedToken);
        if (!this._player || this._player.seeking) {
            return;
        }
        this._getBufferingLatency().then((ms) => {
            this._tracker.trackMsStalled(ms);
        });
    }
    _onPlayedThresholdReached(e) {
        if (this._currentContent && this._player) {
            this.emit(Event.PLAYER_PLAYED_THRESHOLD_REACHED, {
                played: e.data.played,
                threshold: e.data.threshold,
                position: _secToMS(this._player.currentTime),
            });
        }
    }
    /**
     * Called internally by the _onEnded handler and the synthesized ended trigger.
     */
    _emitEnded() {
        if (this._currentContent &&
            this._currentContent.getMediaType() === 'video') {
            this.emit(Event.PLAYER_ENDED_VIDEO, null);
        }
        clearTimeout(this._pauseToken);
        this.emit(Event.PLAYER_ENDED, null);
    }
    /**
     * Emits an error event.
     *
     * @param error - The error object.
     * @param canPlayNext - If true, the player can continue playing the
     *     next track.
     * @param trackData - Optional additional data about the track.
     */
    _emitError(error, canPlayNext, trackData) {
        debugLogger.error('Player Error', error, trackData, canPlayNext);
        this._tracker.setHadError(true);
        // If there's an error with the track, we remove it from the cache, which
        // ensures that the next time this track is played, we will be able to have
        // a fresh track instance.
        if (!this._disableCache && trackData && trackData.fileId) {
            this._cache.remove(trackData.fileId);
        }
        this.emit(Event.PLAYER_ERROR, {
            playId: this._playId,
            error: error,
            track: trackData,
            canPlayNext: canPlayNext,
            position: this._player ? _secToMS(this._player.currentTime) : 0,
        });
    }
    /**
     * Emits an warning event.
     *
     * @param error - The error object.
     * @param trackData - Optional additional data about the track.
     */
    _emitWarning(error, trackData) {
        debugLogger.error('Player Warning', error, trackData, true);
        this._tracker.trackWarning();
        this.emit(Event.PLAYER_WARNING, {
            playId: this._playId,
            error: error,
            track: trackData,
            canPlayNext: true,
            position: this._player ? _secToMS(this._player.currentTime) : 0,
        });
    }
    /**
     * Prepares a player that will be capable to play the provided track.
     *
     * @param _loadingOptions - The options for this playback.
     * @param playId - The id for playback.
     * @return The promise that resolves true if the player is prepared.
     */
    _prepareMediaElement(_loadingOptions, playId) {
        if (playId !== this._playId || !this._upcomingContent) {
            debugLogger.info('Loading content dropped: playId has changed.');
            return Promise.resolve(false);
        }
        return this._recreateMediaElement(this._upcomingContent.getPlayableCodecs(), playId, this._upcomingContent.getMediaType(), this._upcomingContent.getKeySystem() === KeySystem.FAIRPLAY).then((result) => {
            if (!result || playId !== this._playId) {
                debugLogger.info('Loading content dropped after recreate: playId has changed.');
                return Promise.resolve(false);
            }
            const player = this._player;
            if (player &&
                this._upcomingContent &&
                this._upcomingContent.isProtected() &&
                this._emeManager &&
                (!player.mediaKeys ||
                    // @ts-ignore
                    player.mediaKeys.shouldRefreshPerTrack ||
                    this._newMediaKeysPerTrack)) {
                debugLogger.info('Creating media keys');
                return this._emeManager
                    .createMediaKeys(player)
                    .then(() => true)
                    .catch((error) => {
                    error.unrecoverable = true;
                    this._emitError(error, false, this._upcomingContent
                        ? this._upcomingContent.toLogJSON()
                        : undefined);
                    return Promise.reject(error);
                });
            }
            return Promise.resolve(true);
        });
    }
    _handleLoadingComplete(result) {
        const mediaType = this._currentContent && this._currentContent.getMediaType();
        const videoContainer = this._getVideoContainer();
        if (mediaType === 'video' &&
            videoContainer &&
            this._player &&
            this._player.parentNode !== videoContainer) {
            videoContainer.appendChild(this._player);
            this.emit(Event.PLAYER_VIDEO_ELEMENT_APPENDED, null);
        }
        this._upcomingContent = null;
        return result;
    }
    _handleLoadingError(uri, track, playId, error) {
        this._upcomingContent = null;
        if (playId !== this._playId) {
            return Promise.resolve(false);
        }
        this._tracker.trackLoadFailed();
        this.emit(Event.PLAYER_LOADING_FAILED, {
            uri: uri,
            logData: track.logData,
        });
        if (error) {
            // Log the errors
            const trackData = error.track || {
                uri: uri,
                fileId: track.fileId,
                format: track.format,
                deviceId: track.logData.deviceId,
            };
            this._emitError(error, error.name === 'StorageError' ? !!error.canPlayNext : true, trackData);
        }
        return Promise.reject(error);
    }
    _handleLoadedMetadata(playId, logData, loadingOptions) {
        if (playId !== this._playId) {
            debugLogger.info('LoadedMetadata operations dropped: playId has changed.');
            return;
        }
        this._loaded = true;
        const player = this._player;
        if (!player) {
            return;
        }
        // Force this in case something was waiting for metadata.
        this._buffer.dequeueUpdates();
        let initialPosition = loadingOptions.position || 0;
        if (initialPosition > player.duration) {
            initialPosition = 0;
        }
        this._lastTimeUpdatePostion = 0;
        player.currentTime = initialPosition;
        this._tracker.trackLoadDone(_secToMS(player.currentTime));
        // MS browsers don't automatically fire a seeking event when the position is
        // set to 0, so we need to trigger timeupdate ourselves. This unfortunately
        // triggers a bug in other browsers where we append duplicates of the initial
        // fragment. We prevent this by triggering the initial timeupdate a few ms
        // later.
        setTimeout(this._onTimeUpdate, 10);
        this.emitSync(Event.PLAYER_LOAD, {
            autoplay: loadingOptions.autoplay,
            position: _secToMS(initialPosition),
            logData: logData,
        });
        // This handler will be attached to the canplay event on the element. By
        // default we only need to make sure that the starting position is set to
        // the intended position.
        let onCanPlay = () => {
            if (player.currentTime !== initialPosition) {
                debugLogger.info('Resetting initial position.');
                player.currentTime = initialPosition;
            }
        };
        // Play if autoplay
        if (loadingOptions.autoplay && !player.error) {
            // If autoplay is true, we need to do the same starting position check
            // but also trigger playback.
            onCanPlay = () => {
                if (player.currentTime !== initialPosition) {
                    debugLogger.info('Resetting initial position.');
                    player.currentTime = initialPosition;
                }
                if (this._playId !== playId) {
                    debugLogger.info('Play trigger dropped; different playId');
                    return;
                }
                const playPromise = new Promise((resolve) => resolve(this._player && this._player.play()));
                playPromise
                    .catch((e) => {
                    if (e) {
                        if (e.name === 'NotSupportedError') {
                            // There's an issue with the track, do not try to play it again.
                            // Error will be emitted as part of the normal HTMLMediaElement
                            // error event.
                            return Promise.reject(e);
                        }
                        else if (e.name === 'NotAllowedError') {
                            this.emit(Event.PLAYER_AUTOPLAY_FAILED, null);
                            return Promise.reject(e);
                        }
                    }
                    // This was added for cases where the original call to `play()` was
                    // cancelled by a call to `pause()` somewhere.
                    // FIXME: Verify whether this is still the case.
                    if (this._player) {
                        return this._player.play();
                    }
                    return Promise.resolve();
                })
                    .catch(() => {
                    // Since we definitely cannot play, we should inform observers that we
                    // were "paused" by the browser.
                    this._onPause();
                });
            };
        }
        if (player.readyState > 2) {
            // Player state is either HAVE_FUTURE_DATA or HAVE_ENOUGH_DATA
            debugLogger.info('Ready to play, triggering play.');
            onCanPlay();
        }
        else {
            debugLogger.info('Waiting to be playable.');
            this.once(Event.INTERNAL_PLAYER_CANPLAY, onCanPlay);
        }
    }
    _handleCanPlayThrough(playId) {
        if (playId !== this._playId) {
            debugLogger.info('CanPlayThrough operations dropped: playId has changed.');
            return;
        }
        this._tracker.trackCanPlayThrough();
    }
    _loadContent(loadingOptions, playId) {
        if (playId !== this._playId || !this._upcomingContent) {
            debugLogger.info('Loading content dropped: playId has changed.');
            return Promise.resolve(false);
        }
        this._canPreloadEmitted = false;
        const content = this._upcomingContent;
        this._upcomingContent = null;
        this._currentContent = content;
        const callback = loadingOptions.callback;
        if (callback) {
            this.once(Event.PLAYER_LOAD, () => {
                if (!this._playId || playId !== this._playId) {
                    debugLogger.info('Load content event dropped: playId has changed.');
                    // Track changed, do not fire.
                    return;
                }
                callback();
            });
        }
        // We set the load emitter here to ensure that we trigger the loaded
        // event properly. This ensures that if a playback was cancelled, we will
        // not emit the loaded event for that track
        this.once(Event.INTERNAL_PLAYER_LOADED_METADATA, this._handleLoadedMetadata.bind(this, playId, this._currentContent.getLogData(), loadingOptions));
        this.once(Event.INTERNAL_PLAYER_CANPLAYTHROUGH, this._handleCanPlayThrough.bind(this, playId));
        const isProtected = content.isProtected();
        this._tracker.setProtected(isProtected);
        if (content.getKeySystem() === KeySystem.FAIRPLAY) {
            if (content instanceof AudioContent && !isProtected) {
                return Promise.resolve(this._loadUnprotectedTrack());
            }
            return Promise.resolve(this._loadHLSProtectedTrack(loadingOptions, playId));
        }
        else if (content instanceof VideoContent) {
            return this._loadVideoTrack(loadingOptions, playId);
        }
        else if (!isProtected) {
            return Promise.resolve(this._loadUnprotectedTrack());
        }
        return this._loadProtectedTrack(loadingOptions, playId);
    }
    _getBufferingLatency() {
        return new Promise((resolve) => {
            if (!this._player) {
                resolve(0);
                return;
            }
            const bufferingStartTs = Date.now();
            this._player.addEventListener('canplaythrough', function onCanPlay() {
                this.removeEventListener('canplaythrough', onCanPlay);
                resolve(Date.now() - bufferingStartTs);
            });
        });
    }
    _loadVideoTrack(_options, playId) {
        // TODO: Confirm this
        if (!this._player ||
            !this._currentContent ||
            !(this._currentContent instanceof VideoContent)) {
            // FIXME: Throws an error if this happens.
            return Promise.resolve(false);
        }
        const content = this._currentContent;
        debugLogger.info('_loadVideoTrack');
        this._buffer.once(Event.BUFFER_SOURCE_OPEN, () => {
            if (this._currentContent && this._currentContent.isProtected()) {
                this._requestLicense(playId);
            }
        });
        this._player.src = URL.createObjectURL(this._buffer.getMediaSource());
        const videoProfile = content.getVideoProfile();
        const audioProfile = content.getAudioProfile();
        // FIXME: Throws an error if this happens.
        if (!audioProfile || !videoProfile) {
            return Promise.resolve(false);
        }
        this._tracker.trackVideoLoadStart({
            bitrate: videoProfile.video_bitrate,
            audioProfile: audioProfile,
            videoProfile: videoProfile,
        });
        this._tracker.trackBufferLoadStart();
        const initFragment = content.getInitFragment();
        // FIXME: Throws an error if this happens.
        if (!initFragment) {
            return Promise.resolve(false);
        }
        return this._buffer
            .appendFragment(content, initFragment)
            .then(this._buffer.setDuration.bind(this._buffer, content.getCalculatedDuration()))
            .then(() => {
            // TODO: Verify this; looks incorrect.
            if (this._subtitleLanguage) {
                this.setSubtitleLanguage(this._subtitleLanguage);
            }
            return Promise.resolve(true);
        })
            .then(this._onAppendedHeadSegment(playId));
    }
    _loadHLSProtectedTrack(_options, playId) {
        debugLogger.info('Load HLS Protected Track');
        // FIXME: Throws an error if this happens.
        if (!this._player || !this._currentContent) {
            return false;
        }
        const resolvedURL = this._currentContent.getResolvedURL();
        // FIXME: Throws an error if this happens.
        if (!resolvedURL) {
            return false;
        }
        // The initData for Fairplay is not the PSSH, so we need to wait until the
        // encrypted event is emitted before we act.
        const encryptedListener = (ev) => {
            if (!ev.data.fromPolyfill) {
                return;
            }
            this.removeListener(Event.INTERNAL_ENCRYPTED, encryptedListener);
            this._requestLicense(playId, {
                initData: ev.data.initData,
            });
        };
        this.on(Event.INTERNAL_ENCRYPTED, encryptedListener);
        this._tracker.trackBufferLoadStart();
        this._tracker.trackBufferURL(resolvedURL, { bandwidth: 0 });
        this._player.src = this._currentContent.getHLSURL();
        this._player.load();
        return true;
    }
    /**
     * Loads a track that doesn't have EME protection.
     *
     * @return True if the track was loaded, false otherwise.
     */
    _loadUnprotectedTrack() {
        // FIXME: Throws an error if this happens.
        if (!this._player || !this._currentContent) {
            return false;
        }
        const resolvedURL = this._currentContent.getResolvedURL();
        // FIXME: Throws an error if this happens.
        if (!resolvedURL) {
            return false;
        }
        debugLogger.info('_loadUnprotectedTrack', this._currentContent.getURI());
        this._tracker.trackBufferLoadStart();
        this._tracker.trackBufferURL(resolvedURL, { bandwidth: 0 });
        this._player.src = resolvedURL;
        this._player.load();
        return true;
    }
    _onAppendedHeadSegment(playId) {
        return () => {
            if (playId !== this._playId) {
                return false;
            }
            // @ts-ignore
            if (this._player && this._player.spload) {
                // @ts-ignore
                this._player.spload();
            }
            debugLogger.info('Head segment appended.');
            return true;
        };
    }
    /**
     * Loads a track that has EME protection.
     *
     * @param options - The loading options for the track.
     * @param playId - The id for playback.
     * @return Promise that resolve true if the track was loaded, false otherwise
     */
    _loadProtectedTrack(options, playId) {
        if (!this._currentContent ||
            !this._player ||
            !(this._currentContent instanceof AudioContent)) {
            // FIXME: Throws an error if this happens.
            return Promise.resolve(false);
        }
        const content = this._currentContent;
        debugLogger.info('_loadProtectedTrack', content.getURI());
        this._buffer.once(Event.BUFFER_SOURCE_OPEN, this._requestLicense.bind(this, playId, undefined));
        this._player.src = URL.createObjectURL(this._buffer.getMediaSource());
        this._tracker.trackBufferLoadStart();
        let initialAppend = null;
        const initialFragment = content.getFragmentForTime(options.position || 0);
        if (initialFragment === content.getFirstFragment()) {
            debugLogger.info('Appending combined init and playable fragment.');
            initialAppend = this._buffer.appendFragment(content, content.getHeadFragment());
        }
        else {
            debugLogger.info('Appending split init and playable fragment.');
            const initFragment = content.getInitFragment();
            initialAppend = Promise.all([
                // Request and append the head fragment
                initFragment
                    ? this._buffer.appendFragment(content, initFragment).then(() => true)
                    : Promise.resolve(false),
                // Request the initial fragment. This might seem redundant if the item
                // is already preloaded or cached, but this should be cheap.
                initialFragment
                    ? content.getBufferForFragment(initialFragment).then(() => true)
                    : Promise.resolve(false),
            ]);
        }
        return initialAppend.then(this._onAppendedHeadSegment(playId)).then(() => {
            return Promise.resolve(playId === this._playId);
        });
    }
    _requestLicense(playId, overrides = {}) {
        if (playId !== this._playId) {
            const msg = 'Request license dropped: playId has changed.';
            debugLogger.info(msg);
            return Promise.resolve(false);
        }
        // FIXME: Throws an error if this happens.
        if (!this._player || !this._currentContent || !this._emeManager) {
            return Promise.resolve(false);
        }
        const licenseRequestStart = Date.now();
        const mediaKeys = this._player.mediaKeys;
        const content = this._currentContent;
        // Memory leak prevention: cache the local buffers as to not keep references
        // to Content instances.
        const fileId = content.getFileId();
        const logData = content.getLogData();
        const trackLogJSON = content.toLogJSON();
        const keySystem = content.getKeySystem();
        const emeManager = this._emeManager;
        const tracker = this._tracker;
        tracker.setKeySystem(keySystem);
        tracker.setKeySystemImpl(emeManager.getKeySystemImpl());
        return Promise.all([
            content.getLicenseEndpoint() ||
                this._licenseURLResolver.get(keySystem, content.getMediaType()),
            content.getInitParams(),
        ])
            .then(
        // FIXME Remove the boolean after fixing the throws' FIXME.
        ([licenseURL, params]) => {
            tracker.setLicenseSessionLatency(Date.now() - licenseRequestStart);
            const licenseServer = licenseURL.replace(/\{contentId\}/, fileId);
            const initDataType = params.initDataType;
            const initData = overrides.initData || params.initData;
            if (mediaKeys && initData && initDataType) {
                const session = Object.assign(Object.assign({}, params), { playId,
                    initData,
                    mediaKeys,
                    licenseServer,
                    initDataType });
                return emeManager.createSessionWithParams(session);
            }
            // FIXME: Throws an error if this happens.
            // NOTE: Technically this should not happen, but we need to do an early throw here if it is.
            return Promise.resolve(false);
        })
            .then((props) => {
            if (playId !== this._playId) {
                debugLogger.info('License tracking dropped: playId has changed.');
                return Promise.resolve(false);
            }
            if (typeof props !== 'boolean' && 'elapsed' in props) {
                tracker.setLicenseGenerationLatency(props.elapsed.generate);
                tracker.setLicenseRequestLatency(props.elapsed.request);
                tracker.setLicenseUpdateLatency(props.elapsed.update);
            }
            const latency = Date.now() - licenseRequestStart;
            tracker.setKeyLatency(latency);
            this.emit(Event.PLAYER_KEY_RECEIVED, {
                requestTime: latency,
                logData: logData,
            });
            debugLogger.info('License updated.');
            return Promise.resolve(true);
        })
            .catch((err) => {
            if (!err || playId !== this._playId) {
                return Promise.resolve(false);
            }
            const canPlayNext = 
            // @ts-ignore
            err && 'canPlayNext' in err ? err.canPlayNext : true;
            this._emitError(err, canPlayNext, trackLogJSON);
            return Promise.reject(err);
        });
    }
    /**
     * Handles a time update.
     */
    _onTimeUpdate() {
        var _a;
        if (!this._loaded || !((_a = this._player) === null || _a === void 0 ? void 0 : _a.src) || !this._currentContent) {
            return;
        }
        let bitrate = 0;
        const isSeeking = this._player.seeking;
        const currentTime = this._player.currentTime;
        const isVideo = this._currentContent.getMediaType() === 'video';
        // FIXME Add this to the base content class.
        if ('getBitrateForTime' in this._currentContent) {
            bitrate = this._currentContent.getBitrateForTime(this._player.currentTime);
        }
        if (!isSeeking &&
            this._lastTimeUpdatePostion &&
            this._lastTimeUpdatePostion === currentTime) {
            // We got a duplicate event.
            debugLogger.warn('Dropping duplicate time update.');
            return;
        }
        this._lastTimeUpdatePostion = isSeeking ? 0 : currentTime;
        if (this._currentContent.getKeySystem() !== KeySystem.FAIRPLAY &&
            (isVideo || this._currentContent.isProtected())) {
            // Only do buffer progress for protected tracks.
            this._buffer.progress(this._currentContent, isSeeking, currentTime);
        }
        // Events
        const msTime = _secToMS(currentTime);
        const logData = this._currentContent
            ? this._currentContent.getLogData()
            : null;
        if (isSeeking) {
            this._tracker.trackPositionChanged(msTime, this._player.paused);
            this.emit(Event.PLAYER_POSITION_CHANGED, {
                position: msTime,
                logData: logData,
            });
        }
        else {
            this._tracker.trackProgress(msTime, bitrate);
            this.emit(Event.PLAYER_PROGRESS, {
                timestamp: Date.now(),
                position: msTime,
                played: this._tracker.getMSPlayed(),
                interval: 500,
                logData: logData,
            });
        }
        if (!this._canPreloadEmitted &&
            this._player.duration - currentTime <= CAN_PRELOAD_THRESHOLD) {
            this._canPreloadEmitted = true;
            this.emit(Event.PLAYER_CAN_PRELOAD, null);
        }
        else {
            this._canPreloadEmitted = false;
        }
        // Synthesize ended events when necessary.
        clearTimeout(this._syntheticEndedToken);
        if (this._synthesizeEnded &&
            this._currentContent.isProtected() &&
            this._isPlaying()) {
            this._syntheticEndedToken = setTimeout(this._onSyntheticEnded, _secToMS(this._player.duration - currentTime));
        }
    }
    /**
     * Returns whether the player is currently playing.
     *
     * @return True if the player is playing, false otherwise.
     */
    _isPlaying() {
        if (!this._player) {
            return false;
        }
        return !this._player.paused;
    }
    /**
     * Creates an AudioContent.
     *
     * @param uri - The track uri.
     * @param track - Load options for the track.
     * @return a Promise that resolves an AudioContent
     */
    _createAudioContent(uri, track) {
        const measures = {
            manifestLatency: 0,
            resolveLatency: 0,
        };
        const getKeySystemInfo = this._emeManager
            ? this._emeManager.getKeySystemInfo()
            : Promise.resolve({
                keySystem: KeySystem.INVALID_SPOTIFY_KEY,
            });
        return getKeySystemInfo
            .then((config) => {
            const content = AudioContent.create({
                abrManager: this._abrManager,
                keySystem: config.keySystem,
                licenseEndpoint: track.licenseEndpoint,
                transport: this._transport,
                resolver: this._audioResolver,
                uri: uri,
                fileId: track.fileId,
                format: track.format,
                isAd: track.isAd,
                resolvedURL: track.resolvedURL,
                noManifest: track.noManifest,
                preloadedManifest: track.preloadedManifest,
                logData: track.logData,
                disableCache: this._disableCache,
                emitWarning: this._emitWarning,
            });
            return content.load(measures);
        })
            .then((content) => {
            const tracker = this._tracker;
            tracker.setResolveLatency(measures.resolveLatency);
            tracker.setManifestLatency(measures.manifestLatency);
            if (content.isProtected() && track.fileId && !this._disableCache) {
                this._cache.set(track.fileId, content);
            }
            return content;
        });
    }
    /**
     * Creates a VideoContent.
     *
     * @param uri - The track uri.
     * @param track - Load options for the track.
     * @return Promise that resolves a VideoContent.
     */
    _createVideoContent(uri, track) {
        const measures = {
            manifestLatency: 0,
            resolveLatency: 0,
        };
        const fileId = track.fileId;
        const getKeySystemInfo = this._emeManager
            ? this._emeManager.getKeySystemInfo()
            : Promise.resolve({
                keySystem: KeySystem.INVALID_SPOTIFY_KEY,
                audioFormats: [],
                videoFormats: [],
            });
        return getKeySystemInfo
            .then((config) => {
            const content = VideoContent.create({
                uri: uri,
                abrManager: this._abrManager,
                resolver: this._videoResolver,
                keySystem: config.keySystem,
                licenseEndpoint: track.licenseEndpoint,
                fileId: fileId,
                isAd: track.isAd,
                transport: this._transport,
                videoFormats: config.videoFormats,
                audioFormats: config.audioFormats,
                disableCache: this._disableCache,
            });
            return content.load(measures);
        })
            .then((content) => {
            const tracker = this._tracker;
            tracker.setResolveLatency(measures.resolveLatency);
            tracker.setManifestLatency(measures.manifestLatency);
            if (content.isProtected() && fileId && !this._disableCache) {
                this._cache.set(fileId, content);
            }
            return content;
        });
    }
    /**
     * Creates a media element that would most likely be able to play the next
     * track prior to a track actually being loaded.
     *
     * This method is used to optimize the creation of the media element. Normally
     * the player would have to wait until an explicit call to load before
     * creating a media element because we don't know the actual file types that
     * will be played. However, we can make assumptions based on the EMEManager's
     * supported formats.
     *
     * @return A promise that will be fulfilled once the media element has been
     * created.
     */
    _preinitMediaElement() {
        if (this._player || !this._emeManager) {
            return Promise.resolve(this);
        }
        return (this._emeManager
            .getKeySystemInfo()
            .then((info) => {
            var _a, _b;
            const codecs = [];
            if ((_a = info.audioFormats) === null || _a === void 0 ? void 0 : _a[0]) {
                codecs.push(info.audioFormats[0].contentType);
            }
            if ((_b = info.videoFormats) === null || _b === void 0 ? void 0 : _b[0]) {
                codecs.push(info.videoFormats[0].contentType);
            }
            if (!codecs.length) {
                return false;
            }
            return this._recreateMediaElement(codecs, this._playId, codecs.length > 1 ? 'video' : 'audio', info.keySystem !== KeySystem.FAIRPLAY);
        })
            // We technically don't care about errors in this case as it will be
            // handled later.
            .then(() => this, (maybeError) => {
            debugLogger.warn('Cannot precreate media element:', maybeError === null || maybeError === void 0 ? void 0 : maybeError.message);
            return this;
        }));
    }
    /**
     * Get information about the audio codec.
     *
     * @return Player Codec Info
     */
    getCodecInfo() {
        return {
            audiocodec: 'mp3',
            bitrate: 160,
        };
    }
    /**
     * Returns the instance's media source object.
     *
     * @return The instance's internal media source object.
     */
    getMediaSource() {
        return this._buffer.getMediaSource();
    }
    load(track, options, callback) {
        // FIXME: Always use track.uri
        const uriProperty = hasUriProperty(options.uriProperty, track)
            ? options.uriProperty
            : 'uri';
        const uri = track[uriProperty];
        debugLogger.info('load', uri);
        if (!uri) {
            return Promise.reject(new PlaybackError(Errors.PLAYER_CANNOT_FIND_PLAYABLE_URI, 'Cannot find a playable URI.'));
        }
        if (!track.logData) {
            return Promise.reject(Error('Invalid track: logging info not specified'));
        }
        this.stop(track.logData, track.mediaType);
        this._loaded = false;
        this._currentContent = null;
        const playId = ++this._playId;
        const tracker = this._tracker;
        tracker.trackLoadStart(uri, track.fileId, playId.toString(), track.logData);
        this.emit(Event.PLAYER_BEFORE_LOAD, {
            track: track,
            options: options,
            logData: track.logData,
            uri: uri,
            timestamp: Date.now(),
        });
        this._buffer.setPlayId(playId);
        const loadingOptions = {
            position: _msToSec(options.position || 0),
            autoplay: 'autoplay' in options ? options.autoplay : true,
            callback: callback,
        };
        // Set whether the user intends to play the track.
        tracker.setPlayIntended(loadingOptions.autoplay);
        tracker.setPlayedThreshold(options.playedThreshold);
        let trackPromise;
        const cache = this._cache;
        if (track.mediaType === 'video' && !this._emeManager) {
            return Promise.reject(new PlaybackError(Errors.DISALLOW_PROTECTED_TRACK_ERROR, 'Protected tracks not supported'));
        }
        const createFn = track.mediaType === 'video'
            ? this._createVideoContent.bind(this, uri, track)
            : this._createAudioContent.bind(this, uri, track, playId);
        const preloadingContent = this._preloadingTracks[track.fileId];
        const cachedContent = this._disableCache ? null : cache.get(track.fileId);
        if (preloadingContent) {
            debugLogger.info('Waiting for preloading track.', track.fileId);
            trackPromise = preloadingContent.then((measures) => {
                debugLogger.info('Preloading succeeded.', track.fileId);
                tracker.setResolveLatency(measures.resolveLatency);
                tracker.setManifestLatency(measures.manifestLatency);
                return cache.get(track.fileId);
            }, () => {
                // Catch an error
                debugLogger.info('Preloading failed, creating new track', track.fileId);
                return createFn();
            });
        }
        else if (cachedContent) {
            debugLogger.info('Using cached track.', track.fileId, cachedContent);
            tracker.setMemoryCached(true);
            trackPromise = Promise.resolve(cachedContent);
        }
        else {
            debugLogger.info('Creating new track', track.fileId);
            trackPromise = createFn();
        }
        return trackPromise
            .then((content) => {
            if (content.isProtected() && !this._emeManager) {
                return Promise.reject(new PlaybackError(Errors.DISALLOW_PROTECTED_TRACK_ERROR, 'Protected tracks not supported'));
            }
            content.setLogData(track.logData);
            tracker.setCalculatedDuration(_secToMS(content.getCalculatedDuration()));
            this._upcomingContent = content;
            return Promise.resolve();
        })
            .then(this._prepareMediaElement.bind(this, loadingOptions, playId))
            .then(this._loadContent.bind(this, loadingOptions, playId))
            .then(this._handleLoadingComplete, this._handleLoadingError.bind(this, uri, track, playId));
    }
    // TODO: Return a boolean in the future
    preload(track) {
        const uri = track.uri;
        const fileId = track.fileId;
        const cache = this._cache;
        if (!fileId) {
            return Promise.resolve(null);
        }
        if (!this._emeManager) {
            return Promise.resolve(null);
        }
        const canCache = !this._disableCache;
        const isVideo = track.mediaType === 'video';
        // Unencrypted MP3s cannot be preloaded because the URL will expire.
        const canPreloadFormat = track.format === 'MP4' ||
            track.format === 'MP4_DUAL' ||
            track.format === 'MP4_CBCS';
        const shouldPreload = canCache && canPreloadFormat && !isVideo;
        if (!shouldPreload || (canCache && cache.get(fileId))) {
            return Promise.resolve(null);
        }
        const preloadingTracks = this._preloadingTracks;
        if (preloadingTracks[fileId]) {
            return preloadingTracks[fileId];
        }
        debugLogger.info('Preloading track', fileId);
        const measures = {
            manifestLatency: 0,
            resolveLatency: 0,
        };
        const preloadingPromise = this._emeManager
            .getKeySystemInfo()
            .then((config) => {
            const content = AudioContent.create({
                abrManager: this._abrManager,
                keySystem: config.keySystem,
                licenseEndpoint: track.licenseEndpoint,
                transport: this._transport,
                resolver: this._audioResolver,
                uri: uri,
                fileId: fileId,
                format: track.format,
                isAd: track.isAd,
                resolvedURL: track.resolvedURL,
                noManifest: track.noManifest,
                preloadedManifest: track.preloadedManifest,
                logData: track.logData,
                disableCache: this._disableCache,
                emitWarning: this._emitWarning,
            });
            return content.load(measures);
        })
            .then((content) => {
            return Promise.all([
                content,
                track.format !== 'MP4_CBCS'
                    ? content.getBufferForFragment(content.getHeadFragment())
                    : null,
            ]);
        })
            .then(([content]) => {
            cache.set(fileId, content);
            delete preloadingTracks[fileId];
            debugLogger.info('Cached', fileId);
            return measures;
        })
            .catch((e) => {
            delete preloadingTracks[fileId];
            this.emit(Event.PLAYER_PRELOADING_ERROR, {
                error: e,
                track: e.track || null,
                canPlayNext: 'canPlayNext' in e ? e.canPlayNext : true,
                preloading: true,
            });
            return Promise.reject(e);
        });
        preloadingTracks[fileId] = preloadingPromise;
        return preloadingPromise;
    }
    /**
     * Toggle between playing / paused.
     *
     * Pauses the player if it is playing. Resumes playback if the player is paused.
     */
    togglePlay() {
        if (!this._isPlaying()) {
            this.resume();
        }
        else {
            this.pause();
        }
    }
    /**
     * Set language to use for subtitles.
     *
     * @param code - Language code.
     */
    setSubtitleLanguage(code) {
        this._subtitleLanguage = code;
        if (!this._player ||
            !this._currentContent ||
            !(this._currentContent instanceof VideoContent)) {
            return;
        }
        const available = this._currentContent.getSubtitleLanguages();
        if (available && available.indexOf(code) < 0) {
            debugLogger.warn('No available subtitle for language: ', code);
            return;
        }
        const track = document.createElement('track');
        track.label = `${code} subtitles`;
        track.kind = 'subtitles';
        track.srclang = code;
        track.src = this._currentContent.getSubtitleForLanguage(code);
        this._player.appendChild(track);
        if (this._subtitlesVisible) {
            this.showSubtitles();
        }
    }
    /**
     * Set language to use for subtitles.
     *
     * @return List of languages available for the current track.
     */
    getSubtitleLanguages() {
        return this._currentContent instanceof VideoContent
            ? this._currentContent.getSubtitleLanguages()
            : [];
    }
    /**
     * Set the volume of the player.
     *
     * @param amount - Volume between 0.0 (silent) and 1.0 (loudest).
     */
    setVolume(amount) {
        const ev = this.emitSync(Event.PLAYER_BEFORE_VOLUME_CHANGE, {
            volume: amount,
        });
        if (ev.defaultPrevented) {
            return;
        }
        if (amount < 0 || amount > 1) {
            throw new PlaybackError(Errors.PLAYER_ATTEMPTED_VOLUME_OUT_OF_RANGE, 'Volume should be in range [0, 1]');
        }
        this._playerVolume = amount;
        if (this._player) {
            this._player.volume = this._cubicVolume
                ? amount * amount * amount
                : amount;
        }
    }
    /**
     * Get the current volume of the player.
     *
     * @return Volume between 0.0 (silent) and 1.0 (loudest).
     */
    getVolume() {
        return this._playerVolume;
    }
    /**
     * Get the state of the player.
     *
     * @return A Player state.
     */
    getPlayerState() {
        const player = this._player;
        return {
            playing: this._isPlaying(),
            position: !player ? 0 : _secToMS(player.currentTime),
            duration: !player ? 0 : _secToMS(player.duration),
            volume: !player ? 1 : this._playerVolume,
        };
    }
    /**
     * Seek in a track.
     *
     * @todo Consider having the position in seconds instead?
     * @param position - The position to play from (in milliseconds)
     */
    seek(position) {
        if (!this._player) {
            return;
        }
        const playId = this._playId;
        const seeker = () => {
            if (!this._player || playId !== this._playId) {
                debugLogger.info('Seek dropped: playId has changed.');
                return;
            }
            let posInSec = _msToSec(position);
            if (posInSec < 0) {
                posInSec = 0;
            }
            else if (posInSec >= this._player.duration) {
                posInSec = this._player.duration;
            }
            this._player.currentTime = posInSec;
        };
        if (!this._loaded) {
            this.once(Event.PLAYER_LOAD, seeker);
        }
        else {
            seeker();
        }
        this._getBufferingLatency().then((ms) => {
            this._tracker.trackSeekRebuffering(ms);
        });
    }
    /**
     * Pause playback of the track.
     */
    pause() {
        if (this._player && this._isPlaying()) {
            new Promise((resolve) => {
                resolve(this._player && this._player.pause());
            }).catch(function () {
                // Noop
            });
        }
    }
    /**
     * Resume playback of the track.
     */
    resume() {
        if (this._player && !this._isPlaying()) {
            new Promise((resolve) => {
                resolve(this._player && this._player.play());
            }).catch(function () {
                // Noop
            });
        }
    }
    /**
     * Stop playback and unloads the track from the media element.
     *
     * @param logData - The tracks log data.
     * @param nextMediaType - The next media type to play, `audio` or `video`.
     *
     * @return Promise that resolves `true`.
     */
    stop(logData, nextMediaType) {
        const promises = [];
        const _logData = logData || {};
        this.emit(Event.PLAYER_BEFORE_STOP, {
            timestamp: Date.now(),
            logData: _logData,
        });
        this._tracker.trackStopped(logData);
        this._buffer.abort(true);
        // Destroy all media key sessions.
        if (this._emeManager) {
            promises.push(this._emeManager.destroySessions());
        }
        if (this._player) {
            this._fatalOnNextError = this._shouldNextErrorBeFatal();
            if (this._fatalOnNextError) {
                debugLogger.warn('MediaError detected: next immediate media error will be fatal.');
            }
            const url = this._player.src;
            if (BLOB_URL_EXP.test(url)) {
                // Only revoke Blob URLs, otherwise we get warnings on some browsers.
                URL.revokeObjectURL(url);
            }
            this._player.removeAttribute('src');
            this._player.load();
        }
        if (this._currentContent && this._disableCache) {
            this._currentContent.clearCachedBuffers();
        }
        if (this._player &&
            this._currentContent &&
            this._currentContent.getMediaType() === 'video') {
            const textTracks = this._player.children;
            for (let i = 0, len = textTracks.length; i < len; i++) {
                this._player.removeChild(textTracks[i]);
            }
            const videoContainer = this._getVideoContainer();
            if (videoContainer &&
                this._player.parentNode === videoContainer &&
                (this._newElementPerTrack || nextMediaType !== 'video')) {
                videoContainer.removeChild(this._player);
                this.emit(Event.PLAYER_VIDEO_ELEMENT_REMOVED, null);
            }
        }
        this.emit(Event.PLAYER_STOPPED, null);
        return Promise.all(promises).then(() => true);
    }
    /**
     * Hide subtitles.
     */
    hideSubtitles() {
        if (!this._player) {
            return;
        }
        const tracks = this._player.textTracks;
        for (let i = 0, len = tracks.length; i < len; i++) {
            tracks[i].mode = SubtitleMode.HIDDEN;
        }
        this._subtitlesVisible = false;
    }
    /**
     * Show subtitles.
     */
    showSubtitles() {
        if (!this._player) {
            return;
        }
        const tracks = this._player.textTracks;
        for (let i = 0, len = tracks.length; i < len; i++) {
            const track = tracks[i];
            if (track.language === this._subtitleLanguage) {
                track.mode = SubtitleMode.SHOWING;
            }
            else {
                track.mode = SubtitleMode.HIDDEN;
            }
        }
        this._subtitlesVisible = true;
    }
    /**
     * Returns the KeySystemInfo associated with the current player.
     *
     * @return A promise that resolves the key system info or null otherwise.
     */
    getKeySystemInfo() {
        return this._emeManager
            ? this._emeManager.getKeySystemInfo()
            : Promise.resolve(null);
    }
    /**
     * Returns list of available Video Profiles for the currently playing Video.
     *
     * @return List of currently available video profiles.
     */
    getVideoVariants() {
        if (!this._currentContent ||
            !(this._currentContent instanceof VideoContent)) {
            return [];
        }
        const result = [];
        const profiles = this._currentContent.getVideoProfiles();
        for (let i = 0, len = profiles.length; i < len; i++) {
            const profile = profiles[i];
            result.push({
                width: profile.video_width,
                height: profile.video_height,
                bitrate: profile.video_bitrate,
            });
        }
        return result;
    }
    /**
     * Set the preferred video profile, will override adaptive bitrate playback.
     *
     * @param bitrate - The preferred bitrate.
     * @return Promise that resolves operation result.
     */
    setPreferredBitrate(bitrate) {
        if (typeof bitrate !== 'number') {
            return Promise.resolve(OperationResult.INVALID);
        }
        if (this._currentContent &&
            this._currentContent.getMediaType() === 'video' &&
            bitrate > 0) {
            this._buffer.abort(true);
        }
        this._abrManager.overrideBitrate(bitrate);
        return Promise.resolve(OperationResult.SUCCESS);
    }
    /**
     * Activates the current media element.
     *
     * This function must be called as part of a synchronous process such as a
     * user clicking a button or a user control.
     *
     * @return True if the element was activated, false if there is no media
     * element, if it is too late in the process to activate the media element,
     * or if the media element is already active.
     */
    activateElement() {
        if (this._playerActivated || !this._player || this._player.src) {
            return false;
        }
        this._player.load();
        this._playerActivated = true;
        return true;
    }
    /**
     * Get the information about the current media configuration for the browser.
     *
     * @return `MediaConfig` if the config was found, null otherwise.
     */
    getMediaConfig() {
        if (this._mediaConfig === undefined) {
            return _getMediaConfig(this._emeManager, this._player).then((mediaConfig) => {
                this._mediaConfig = mediaConfig;
                return this._mediaConfig;
            });
        }
        return Promise.resolve(this._mediaConfig);
    }
}
//# sourceMappingURL=player.js.map